import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Save, Loader2 } from "lucide-react";

const Field = ({ label, value, onChange, testid, disabled }) => (
  <div>
    <Label className="text-[13px] text-[#4B5563]">{label}</Label>
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="mt-1 h-11" data-testid={testid} />
  </div>
);

export default function ExternalProfile() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/external/me").then(({ data }) => {
      setMe(data);
      setForm({
        full_name: data.full_name, organization: data.organization, contact_role: data.contact_role,
        phone: data.phone, website: data.website, service_area: data.service_area,
      });
    }).catch(() => {});
  }, []);

  const canEdit = me?.permissions?.edit_profile !== false;
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try { await api.put("/external/profile", form); toast.success("Profile updated."); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!me) return <div className="space-y-3 max-w-2xl"><Skeleton className="h-11" /><Skeleton className="h-11" /><Skeleton className="h-11" /></div>;

  return (
    <div className="max-w-2xl" data-testid="external-profile-page">
      <h1 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2 mb-1"><Building2 size={20} /> Your organization</h1>
      <p className="text-[15px] text-muted-foreground mb-5">{me.account_type_label} · keep your contact details current for the MovingMyMama team.</p>
      <Card className="p-6 border-[#E6EDF2] space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Organization" value={form.organization} onChange={set("organization")} testid="external-profile-org" disabled={!canEdit} />
          <Field label="Contact name" value={form.full_name} onChange={set("full_name")} testid="external-profile-name" disabled={!canEdit} />
          <Field label="Contact role" value={form.contact_role} onChange={set("contact_role")} testid="external-profile-role" disabled={!canEdit} />
          <Field label="Phone" value={form.phone} onChange={set("phone")} testid="external-profile-phone" disabled={!canEdit} />
          <Field label="Website" value={form.website} onChange={set("website")} testid="external-profile-website" disabled={!canEdit} />
          <Field label="Service area" value={form.service_area} onChange={set("service_area")} testid="external-profile-area" disabled={!canEdit} />
        </div>
        <div className="text-[13px] text-muted-foreground">
          Email: <span className="font-medium text-[#252221]">{me.email}</span> · Type: {me.account_type_label}
          {me.provider_type ? ` · ${String(me.provider_type).replace(/_/g, " ")}` : ""}
        </div>
        {canEdit && (
          <div className="flex justify-end pt-1">
            <Button onClick={save} disabled={saving} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 h-11 rounded-full px-6" data-testid="external-profile-save-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
